// VideoCard.jsx
import React, { useState } from "react";
import styled from "styled-components";
import { imageZoomEffect } from "./ReusableStyles";

import {
  CardInfo,
  Description,
  ButtonContainer,
  StyledButton,
  Banner as VideoBanner,
} from "./CardStyles";

const VideoWrapper = styled.div`
  height: 13rem;
  width: 100%;
  position: relative;
  border-radius: 5px;
  overflow: hidden;
  background: #000;
  cursor: pointer;
  ${imageZoomEffect};

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: brightness(60%);
    transition: filter 0.3s ease;
  }

  &:hover video {
    filter: brightness(80%);
  }
`;

const PlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  background: rgba(68, 253, 71, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  transition: all 0.3s ease;
  z-index: 2;

  &:after {
    content: "";
    width: 0;
    height: 0;
    border-left: 16px solid #000;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    margin-left: 4px;
  }

  ${VideoWrapper}:hover & {
    background: #44fd47;
    transform: translate(-50%, -50%) scale(1.1);
  }
`;

const ReplayText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(68, 253, 71, 0.9);
  color: #000;
  font-family: "Orbitron", sans-serif;
  font-size: 0.8rem;
  font-weight: bold;
  padding: 0.5rem 1rem;
  border-radius: 30px;
  text-transform: uppercase;
  pointer-events: none;
  z-index: 2;

  ${VideoWrapper}:hover & {
    background: #44fd47;
  }
`;

export const VideoCard = ({
  videoSrc,
  poster,
  name,
  desc,
  link,
  link2,
  hasBanner,
  bannerText,
  githubOnly,
  hideButtons,
  onLinkClick,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasEnded, setHasEnded] = useState(false);

  const handleClick = (e) => {
    const video = e.currentTarget.querySelector("video");
    if (!video) return;

    if (hasEnded || !isPlaying) {
      video.currentTime = 0;
      video.play();
      setIsPlaying(true);
      setHasEnded(false);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setHasEnded(true);
  };

  return (
    <div className="service">
      <VideoWrapper onClick={handleClick}>
        {hasBanner && (
          <VideoBanner
            backgroundColor={
              bannerText === "Web App" ? "#ffe501" :
              bannerText === "Custom GPT" ? "#32CD32" :
              bannerText === "NPM Package" ? "#00d9ff" :
              bannerText === "Browser Extension" ? "#ff4b4b" :
              bannerText === "Demo" ? "#ffe501" :
              "#ff4b4b"
            }
            textColor={
              bannerText === "Web App" ? "#000000" :
              bannerText === "Custom GPT" ? "#ffffff" :
              bannerText === "NPM Package" ? "#000000" :
              bannerText === "Browser Extension" ? "#ffffff" :
              bannerText === "Demo" ? "#000000" :
              "#ffffff"
            }
          >
            {bannerText}
          </VideoBanner>
        )}
        <video
          src={videoSrc}
          poster={poster}
          playsInline
          preload="auto"  // ← Preload for instant play
          onEnded={handleEnded}
        />
        {!isPlaying && !hasEnded && <PlayButton />}
        {hasEnded && <ReplayText>Replay</ReplayText>}
      </VideoWrapper>

      <CardInfo>{name}</CardInfo>
      <div className="description-container">
        {desc && <Description>{desc}</Description>}
      </div>

      <ButtonContainer>
        {!hideButtons && (
          <>
            {name === "Homeschool Guide AI" ? (
              <StyledButton onClick={() => onLinkClick(link)}>View Live</StyledButton>
            ) : githubOnly ? (
              <StyledButton onClick={() => onLinkClick(link)}>GitHub</StyledButton>
            ) : (hasBanner && bannerText === "Custom GPT") ? (
              <StyledButton onClick={() => onLinkClick(link)}>Try Out</StyledButton>
            ) : (
              <>
                <StyledButton onClick={() => onLinkClick(link)}>Preview</StyledButton>
                {link2 && (
                  <StyledButton as="div" onClick={() => onLinkClick(link2)}>
                    Download
                  </StyledButton>
                )}
              </>
            )}
          </>
        )}
      </ButtonContainer>
    </div>
  );
};